<template>
    <div class="star-container">
      <span v-for="index in maxRating" :key="index"
        :class="getStarClass(index)"
        @click="rate(index)"
        v-if="!readOnly"
        aria-label="'Rate ' + index + ' stars'">
      </span>
      <span v-for="index in maxRating" :key="index"
        :class="getStarClass(index)"
        v-if="readOnly"
        aria-label="'Rate ' + index + ' stars'">
      </span>
      <span style="font-style: italic;">{{ formattedRating }}</span>
    </div>
  </template>
  
  <script setup>
  import { ref, watch, computed } from 'vue';
  
  const props = defineProps({
    initialRating: { type: Number, default: 0 },
    maxRating: { type: Number, default: 5 },
    readOnly: { type: Boolean, default: false }
  });
  
  const emits = defineEmits(['update:rating']);
  
  const internalRating = ref(Number(props.initialRating));

const formattedRating = computed(() => {
  const rating = Number(internalRating.value); // Convert to number to be safe
  if (rating % 1 === 0) {
    return rating.toFixed(0); // No decimal part
  }
  return rating.toFixed(1); // One decimal place
});
  
  watch(() => props.initialRating, (newVal) => {
    internalRating.value = newVal;
  });
  
  function rate(index) {
    if (!props.readOnly) {
      internalRating.value = index;
      emits('update:rating', index);
    }
  }
  
  function getStarClass(index) {
    if (index <= Math.floor(internalRating.value)) {
      return 'rating-star-full';
    } else if (index === Math.ceil(internalRating.value) && internalRating.value % 1 > 0) {
      return 'rating-star-half';
    } else {
      return 'rating-star-empty';
    }
  }
  </script>
  
  
  <style lang="scss" scoped>
  @import '~/assets/constants.scss';
  .rating-star-empty:before {
    font-family: 'FontAwesome';
    content: "\f006";
    font-weight: 400;
    color: $fa-dark-gray;
  }
  
  .dark-mode .rating-star-empty {
    color: $light-white;
  }
  
  .rating-star-full::before {
    font-family: 'FontAwesome';
    content: "\f005";
    color: $fa-orange;
  }
  
  .star-container {
    display: grid;
    grid-gap: 0.25rem;
    grid-auto-flow: column;
    width: min-content;
  }

  .rating-star-half::before {
  font-family: 'FontAwesome';
  content: "\f123"; // FontAwesome half-star icon code
  color: $fa-orange;
}

.star-container span:not(.rating-star-empty) {
  cursor: pointer;
}

// Cursor style for read-only stars
.star-container.read-only span {
  cursor: default;
}
  </style>